export const API_BASE =
  import.meta.env.VITE_API_URL ||
  "https://stacked-backend-gv43.onrender.com/api/v1";

export const getToken = () =>
  localStorage.getItem("__admin_token__") ||
  localStorage.getItem("__login_user_token__");

// ── Refresh-on-401, shared across all authedFetch callers ──
// Without this, every non-RTK-Query caller (Connected Accounts page,
// Suspicious Activity, a bunch of admin screens) silently fails the
// moment the 15-minute access token expires, because they just see a
// 401 toast and never retry. RTK Query's baseQueryWithReauth handles
// this for slice-based callers; this gives the raw-fetch path the
// same behavior so the user experience is uniform.
//
// Dedupe with a single in-flight promise so 10 simultaneous
// authedFetch calls fire ONE /auth/refresh, not 10. Otherwise the
// refresh-token-reuse detection on the backend would trip and revoke
// the whole family, kicking the user out.
let refreshInFlight = null;

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem("__refresh_token__");
  if (!refreshToken) return null;

  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    const next = data?.data?.token;
    const nextRefresh = data?.data?.refreshToken;
    if (!next || !nextRefresh) return null;

    // Mirror api.config.js — persist the new token pair so subsequent
    // calls (RTK Query, authedFetch, page reload) all see the same
    // fresh state. Stripe-token-rotation pattern.
    localStorage.setItem("__login_user_token__", next);
    localStorage.setItem("__refresh_token__", nextRefresh);
    return next;
  } catch {
    return null;
  }
}

export const authedFetch = async (url, options = {}) => {
  const buildInit = (token) => {
    const headers = { ...(options.headers || {}) };

    // Strip any caller-supplied auth header (any casing) — authedFetch
    // owns auth. Several pages passed `Authorization: Bearer <raw
    // localStorage value>` alongside our injected lowercase
    // `authorization`; fetch merges the duplicate names into one
    // comma-joined header the backend can't parse, which surfaced as
    // random 401s (e.g. the Favorites page clients popover).
    for (const k of Object.keys(headers)) {
      if (k.toLowerCase() === "authorization") delete headers[k];
    }

    // Default Content-Type to application/json for string bodies when the
    // caller didn't set one. Without this, the browser labels a JSON
    // string body as `text/plain`, Express's express.json() skips
    // parsing, and the handler sees an empty req.body — which surfaces as
    // confusing 400s like the demo CRM switcher's `Unknown vertical ""`.
    // Only string bodies are touched: FormData / Blob uploads must keep
    // their browser-managed multipart Content-Type + boundary, and any
    // caller that set its own Content-Type wins (it's spread above).
    const hasContentType = Object.keys(headers).some(
      (h) => h.toLowerCase() === "content-type"
    );
    if (typeof options.body === "string" && !hasContentType) {
      headers["Content-Type"] = "application/json";
    }

    if (token) headers.authorization = `Bearer ${token}`;
    return { ...options, headers };
  };

  let res = await fetch(url, buildInit(getToken()));

  // If unauthorized, try a single refresh-and-retry.
  if (res.status === 401) {
    if (!refreshInFlight) {
      refreshInFlight = refreshAccessToken().finally(() => {
        refreshInFlight = null;
      });
    }
    const newToken = await refreshInFlight;
    if (newToken) {
      res = await fetch(url, buildInit(newToken));
    } else {
      // Refresh failed — the session is truly dead. Mirror the
      // baseQueryWithReauth final-failure path in api.config.js so
      // raw-fetch callers behave the same as RTK Query callers
      // (toast + clearStorage + redirect to /login). Previously
      // each caller had to handle this individually, which was
      // both noisy and inconsistent — some pages would silently
      // 401-loop instead of bouncing to login.
      try {
        // Avoid recursive imports — pull localStorage util lazily.
        const { clearStorage } = await import("./localStorage.js");
        clearStorage();
      } catch { /* no-op */ }
      if (typeof window !== "undefined") {
        // Only redirect if we're not already on the login page,
        // and not already navigating away — otherwise the toast
        // never gets shown and we ping-pong.
        if (!window.location.pathname.startsWith("/login")) {
          if (window.toast && window.toast.error) {
            window.toast.error("Your session has expired. Please log in again.");
          }
          setTimeout(() => {
            window.location.href = "/login";
          }, 800);
        }
      }
    }
  }

  return res;
};
